/**
 * DTOs and view models for the topic-scoped advisor↔AI chat ("KI-Beratung").
 * Mirrors the `ai` service contract behind `${apiEndpoint}/ai/chat`.
 */

/** Conversation scope. For the Vertragscheck feature this is always `CONTRACT_ANALYSIS`. */
export type AiConversationTopic =
  'CONTRACT_ANALYSIS' | 'CONTRACT_DISCUSSION' | 'PORTFOLIO_CALL' | 'CASHFLOW_ANALYSIS' | 'DOCUMENT_HELPER' | 'GENERIC';

/** Topics whose fresh conversation is opened by the AI itself (backend kickoff) and that have no smart prompt. */
export const KICKOFF_TOPICS: readonly AiConversationTopic[] = ['CONTRACT_DISCUSSION', 'PORTFOLIO_CALL'];

export type ChatRole = 'USER' | 'ASSISTANT';

/** One stored turn of the conversation. */
export interface ChatMessageDto {
  role: ChatRole;
  content: string;
  createdAt: string;
  /** Set for document-attachment entries (their `content` is empty) — rendered as a document chip. */
  documentName?: string | null;
}

/** The currently accepted "smart prompt" (advisor context) for a topic + subject. */
export interface DigestDto {
  topic: string;
  subjectId: string;
  contextText: string;
  sourceConversationId: string | null;
  updatedAt: string;
}

/** Response of `GET /ai/chat/topic/{topic}` — the get-or-create scoped conversation. */
export interface ConversationContextDto {
  conversationId: string;
  topic: AiConversationTopic;
  subjectId: string | null;
  messages: ChatMessageDto[];
  activeDigest: DigestDto | null;
}

// ── Request bodies ──────────────────────────────────────────────────────────

/** Body of `POST /ai/chat/question`. */
export interface AskQuestionRequest {
  question: string;
  conversationId: string;
}

/** Body of `PUT /ai/chat/digest/{topic}/{subjectId}`. */
export interface AcceptDigestRequest {
  contextText: string;
}

/** Response of `POST /ai/chat/{conversationId}/document` — the ingested attachment. */
export interface DocumentUploadResponse {
  fileName: string;
  pageCount: number;
  ocrUsed: boolean;
  tokensUsed: number;
  remainingTokens: number;
}

// ── View models ─────────────────────────────────────────────────────────────

/**
 * A single rendered chat bubble. `content` is what is currently shown; `target` is the full text
 * received so far — the typewriter reveals `content` toward `target` for a "being written" effect.
 * `streaming` marks the in-progress assistant answer (still receiving from the backend).
 */
export interface ChatBubble {
  role: ChatRole;
  content: string;
  target?: string;
  streaming?: boolean;
  /** Set for document-attachment turns — the bubble renders a file chip instead of `content`. */
  documentName?: string | null;
}

/**
 * Composer attachment chip — a document uploaded into the conversation context. Chips stay in the
 * composer strip until the next message is sent; sending moves them into the transcript as document
 * bubbles right above that message (matching the persisted order a reload produces).
 */
export interface DocumentChip {
  fileName: string;
  status: 'uploading' | 'ready';
  pageCount?: number;
}

/** How a streaming/HTTP failure maps to a UX action. */
export type AiStreamErrorKind = 'QUOTA' | 'NO_PLAN' | 'GENERIC';

/** Error surfaced by the streaming endpoints. */
export interface AiStreamError {
  kind: AiStreamErrorKind;
}
